// src/lib/resident.ts — the resident portal (Task J): sign-up with the invite code, documents, notices, "my building",
// and the staff side of each (documents panel, invite card, notices tab). Every call is checked server-side; a resident
// only ever gets their own records and never an ID / right to rent / references document.
import { api } from './api';
import { useAuth } from '../stores/auth';
import { downscaleImage } from './parking';
import { fileToUpload, openStoredFile } from './reservations';

export type DocumentCategory = 'CONTRACT' | 'DEPOSIT_CERT' | 'PRESCRIBED_INFO' | 'INVENTORY' | 'SAFETY_CERT' | 'INVOICE' | 'LETTER' | 'ID' | 'RIGHT_TO_RENT' | 'REFERENCE' | 'QUOTE' | 'RECEIPT' | 'OTHER';
/** The categories on a tenancy, flat or building (QUOTE and RECEIPT belong to bills only). */
export const DOCUMENT_CATEGORIES: Array<[DocumentCategory, string]> = [
  ['CONTRACT', 'Tenancy agreement'], ['DEPOSIT_CERT', 'Deposit protection'], ['PRESCRIBED_INFO', 'Deposit — prescribed information'], ['INVENTORY', 'Inventory / check-in report'], ['SAFETY_CERT', 'Safety certificate'],
  ['INVOICE', 'Invoice'], ['LETTER', 'Letter'], ['ID', 'ID / passport'], ['RIGHT_TO_RENT', 'Right to rent'], ['REFERENCE', 'References'], ['OTHER', 'Other'],
];
/** A bill's attachments: the supplier's invoice, a quote, a receipt. Finance only, never shown to a resident. */
export const BILL_DOCUMENT_CATEGORIES: Array<[DocumentCategory, string]> = [['INVOICE', 'Invoice'], ['QUOTE', 'Quote'], ['RECEIPT', 'Receipt'], ['OTHER', 'Other']];
export const HIDDEN_CATEGORIES: DocumentCategory[] = ['ID', 'RIGHT_TO_RENT', 'REFERENCE'];
export type DocumentResource = 'tenancy' | 'unit' | 'property' | 'work_order' | 'bill';
/** What one upload may weigh (src/utils/storage.ts MAX_FILE_BYTES). */
export const MAX_UPLOAD_BYTES = 7 * 1024 * 1024;

/** Open a document: a stored file through the authenticated /files route, an external link (migrated bill links) as it is. */
export function openDocument(url: string): Promise<void> {
  if (url.startsWith('/api/v1/files/')) return openStoredFile(url);
  window.open(url, '_blank', 'noopener,noreferrer');
  return Promise.resolve();
}

export interface DocumentItem {
  id: string; resource: DocumentResource; resourceId: string; tenancyId: string | null; unitId: string | null; propertyId: string | null;
  category: DocumentCategory; categoryLabel: string; name: string; url: string; mimeType: string | null; sizeBytes: number | null;
  visibleToTenant: boolean; notes: string | null; uploadedBy: string | null; uploadedByName: string | null; uploadedAt: string | null; attachedTo: string;
}
export interface DueState { key: 'overdue' | 'soon' | 'ok' | 'none'; label: string; days: number | null }
export interface MyDocuments {
  documents: DocumentItem[];
  deposits: Array<{ tenancyId: string; tenancyReference: string; scheme: string | null; schemeReference: string; protectedOn: string | null; amountMinor: number; currency: string; status: string; name: string }>;
  certificates: Array<{ id: string; type: string; label: string; where: 'building' | 'flat'; propertyName: string; unitReference: string | null; reference: string | null; expiresOn: string | null; state: DueState; documentUrl: string | null }>;
  invoices: Array<{ id: string; number: string | null; issueDate: string | null; dueDate: string | null; totalMinor: number; outstandingMinor: number; currency: string; status: string }>;
  letters: Array<{ id: string; subject: string; sentAt: string | null; templateKey: string | null; kind: string | null }>;
  tenancies: Array<{ id: string; reference: string; propertyName: string; unitReference: string; status: string }>;
  note: string;
}
export interface Announcement {
  id: string; propertyId: string | null; propertyName: string | null; title: string; body: string; status: 'DRAFT' | 'LIVE' | 'EXPIRED';
  publishedAt: string | null; publishedByName: string | null; expiresAt: string | null; createdAt: string | null; createdByName: string | null; updatedAt: string | null;
}
export interface MyBuilding {
  propertyId: string; propertyName: string; address: string | null; unitReference: string; tenancyId: string; tenancyReference: string;
  contacts: Array<{ role: string; name: string | null; phone: string | null; email: string | null; emergency: boolean }>;
  sections: Array<{ key: string; title: string; items: Array<{ label: string; value: string }> }>;
  notices: Announcement[];
  parking: { bay: string; kind: string; vehicleReg: string | null; permit: string | null; since: string } | null;
  published: boolean;
  /** The building's gallery from the media library (public image paths), in the order staff set. */
  photos: Array<{ url: string; thumbUrl: string; label: string | null; caption: string | null; width: number | null; height: number | null }>;
}
export interface InviteInfo { firstName: string | null; unit: string; scheme: string; needsDetails: boolean; expiresAt: string | null }
export interface InviteStatus {
  tenancyId: string; code: string | null; invitedAt: string | null; expiresAt: string | null; acceptedAt: string | null; acceptedByName: string | null;
  state: 'NOT_ISSUED' | 'LIVE' | 'EXPIRED' | 'USED' | 'ENDED'; signupUrl: string | null;
  tenants: Array<{ userId: string; name: string | null; email: string | null; registered: boolean; lead: boolean }>;
  letters: Array<{ id: string; status: string; toEmail: string | null; sentAt: string | null; createdAt: string | null }>;
}
export interface InviteResult { tenancyId: string; code: string; expiresAt: string | null; signupUrl: string; sent: number; letters: Array<{ id: string; toEmail: string | null; status: string; error: string | null }> }
export interface Upload { fileName: string; contentType: string; dataBase64: string }

/** A file ready to upload: photos are shrunk to ≤ 1600 px in the browser first (JPEG), anything else goes as it is. */
export async function toUpload(file: File): Promise<Upload> {
  if (file.type.startsWith('image/') && file.type !== 'image/heic') {
    const img = await downscaleImage(file, 1600);
    return { fileName: img.fileName, contentType: img.contentType, dataBase64: img.dataBase64 };
  }
  return fileToUpload(file);
}

export const documentsApi = {
  list: async (q: { tenancyId?: string; unitId?: string; propertyId?: string; workOrderId?: string; billId?: string; includeRelated?: boolean }) =>
    (await api.get<DocumentItem[]>('/documents', q)).data,
  mine: async () => (await api.get<MyDocuments>('/documents/mine')).data,
  letter: async (id: string) => (await api.get<{ id: string; subject: string; body: string; sentAt: string | null; toEmail: string | null }>(`/documents/mine/letters/${id}`)).data,
  upload: async (input: { resource: DocumentResource; resourceId: string; category?: DocumentCategory; name?: string | null; visibleToTenant?: boolean; file: Upload }) =>
    (await api.post<DocumentItem>('/documents', input)).data,
  update: async (id: string, patch: { name?: string; category?: DocumentCategory; visibleToTenant?: boolean }) => (await api.patch<DocumentItem>(`/documents/${id}`, patch)).data,
  remove: async (id: string) => (await api.delete<{ id: string }>(`/documents/${id}`)).data,
};

export const announcementsApi = {
  list: async (q: { propertyId?: string; status?: string } = {}) => (await api.get<Announcement[]>('/announcements', q)).data,
  mine: async () => (await api.get<Announcement[]>('/announcements/mine')).data,
  create: async (input: { propertyId: string | null; title: string; body: string; expiresAt?: string | null; publish?: boolean }) => (await api.post<Announcement>('/announcements', input)).data,
  update: async (id: string, patch: { title?: string; body?: string; expiresAt?: string | null; propertyId?: string | null }) => (await api.patch<Announcement>(`/announcements/${id}`, patch)).data,
  publish: async (id: string) => (await api.post<Announcement>(`/announcements/${id}/publish`)).data,
  unpublish: async (id: string) => (await api.post<Announcement>(`/announcements/${id}/unpublish`)).data,
  remove: async (id: string) => (await api.delete<{ id: string }>(`/announcements/${id}`)).data,
};

export const portalApi = {
  myBuilding: async () => (await api.get<MyBuilding[]>('/my-building')).data,
};

export const inviteApi = {
  validate: async (code: string) => (await api.public.get<InviteInfo>(`/auth/invite/${encodeURIComponent(code)}`)).data,
  accept: async (code: string, body: { password: string; phone?: string | null; acceptTerms: boolean; firstName?: string; lastName?: string; email?: string }) =>
    (await api.public.post<{ accessToken: string; refreshToken: string; user: unknown; tenancyId: string }>(`/auth/invite/${encodeURIComponent(code)}/accept`, body)).data,
  status: async (tenancyId: string) => (await api.get<InviteStatus>(`/tenancies/${tenancyId}/invite`)).data,
  resend: async (tenancyId: string) => (await api.post<InviteResult>(`/tenancies/${tenancyId}/invite/resend`)).data,
  invite: async (tenancyId: string, body: { email: string; firstName: string; lastName?: string; phone?: string }) => (await api.post<InviteResult & { userId: string; created: boolean }>(`/tenancies/${tenancyId}/invite`, body)).data,
};

export const fileSize = (n: number | null) => (n === null ? '' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
export const dueTone = (k: DueState['key']) => (k === 'overdue' ? 'EXPIRED' : k === 'soon' ? 'EXPIRING' : 'VALID');

/**
 * A resident's portal (own-records scope on the residents page, no My Day): AppLayout hands them the portal frame
 * (components/tenant/TenantShell — the phone app's look, one navigation) and Messages takes the portal's layout.
 */
export function useResidentPortal() {
  const { user, canAccessPage } = useAuth();
  return user?.scope.type === 'OWN' && canAccessPage('residents') && !canAccessPage('myDay');
}

/** An applicant (signed up in the app to find a home, not a resident yet): applicantPortal, own scope, no staff day. */
export function useApplicantPortal() {
  const { user, canAccessPage } = useAuth();
  return user?.scope.type === 'OWN' && canAccessPage('applicantPortal') && !canAccessPage('residents') && !canAccessPage('myDay');
}
